import {useComputed, useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'

import moment from 'moment'
import {addLocale} from 'primereact/api'
import {Avatar} from 'primereact/avatar'
import {Button} from 'primereact/button'
import {Calendar} from 'primereact/calendar'
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup'
import {Dialog} from 'primereact/dialog'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {InputTextarea} from 'primereact/inputtextarea'
import {MultiSelect} from 'primereact/multiselect'
import {Tag} from 'primereact/tag'
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {useDebouncedCallback} from 'use-debounce'
import {HTTP_STATUS_OK} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {getLanguageCode} from '../../i18n/LanguageUtils'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {capitalize} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import {bgLocale} from '../form-utils/CalendarConstant'
import ImageUploader from '../image/ImageUploader'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import StyledPatientOptions from '../styled-component/styled-options/StyledPatientOptions'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import CalendarFormSummary from './CalendarFormSummary'
import CustomToggleWitches from './CustomToggleWitches'
import {
    CalendarFormType,
    componentTypes,
    dialogMedicineContent,
    getEarlyReminderOptions,
    reminderKeys,
    reminderTypes,
} from './ReminderConstants'
import {
    useRecipientsOptions,
    useReminderCRUD,
} from './query-keys/ReminderKeysManager'

const dateTimeFormat = 'yyyy-MM-DD HH:mm'
const dateFormat = 'yyyy-MM-DD'
const timeFormat = 'HH:mm'
const customLabelClassName =
    'font-bold col-12 p-0 text-sm sm:text-sm md:text-xl lg:text-xl'

const initialReminderData = {
    oneTime: true,
    [reminderKeys.type]: reminderTypes.reminder,
    // [reminderKeys.dateTime]: new Date(),
    // [reminderKeys.startDate]: new Date(),
    // [reminderKeys.endDate]: (() => {
    //     const currentTime = new Date()
    //     currentTime.setDate(currentTime.getDate() + 1)
    //     return currentTime
    // })(),
    // [reminderKeys.time]: (() => {
    //     const currentTime = new Date()
    //     currentTime.setDate(currentTime.getDate() + 1)
    //     return currentTime
    // })(),
    [reminderKeys.isRemindEarly]: false,
    [reminderKeys.earlyReminder]: 1,
}
const initialAppointmentData = {
    oneTime: true,
    [reminderKeys.earlyReminder]: 0,
    [reminderKeys.type]: reminderTypes.appointment,
    [reminderKeys.timeOrDay]: 'time',
    // [reminderKeys.dateTime]: new Date(),
    [reminderKeys.isRemindEarly]: false,
}

const userType = {
    user: 'user',
    generalPractitioner: 'generalPractitioner',
    nextOfKin: 'nextOfKin',
}

const CalendarForm = forwardRef(
    ({calendarFormType = CalendarFormType.patient}, ref) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            showForm: handleShowForm,
            hideForm: handleHideForm,
        }))
        //#region Translate
        const {translateCommons} = TranslateUtils()
        //#endregion

        //#region Context
        const {currentViewedUser} = usePatientContext()
        const {user} = useAuth()
        const {timezoneId} = useAppConfig()
        //#endregion

        //#region State
        const visible = useSignal(false)
        const data = useSignal({
            [reminderKeys.type]:
                calendarFormType === CalendarFormType.reservation
                    ? reminderTypes.appointment
                    : reminderTypes.reminder,
        })
        const additionalKeys = useSignal([])
        //#endregion

        //#region Locale
        addLocale('bg', bgLocale)
        //#endregion

        //#region Ref
        const switchRef = useRef([])
        const imgRef = useRef([])
        const calendarRef = useRef(null)
        //#endregion

        //#region Query
        const queryClient = useQueryClient()
        const {data: recipientsData} = useRecipientsOptions(
            user?.id,
            data.value?.userId,
            data.value[reminderKeys.type] === reminderTypes.appointment,
        )

        const {mutateAsync: createOrUpdateReminder} = useReminderCRUD(
            !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
            user?.id,
        )

        const {mutateAsync: deleteReminder} = useReminderCRUD(
            QueryCRUDTypes.delete,
            user?.id,
        )
        //#endregion

        //#region Handle

        const handleOnChangeTime = (
            timePickerElement,
            valueText,
            key,
            type,
        ) => {
            // Format Hours, Minutes to Date from Editable Content Time Select
            let dateValue = handleGetDateValue(data?.value[key]) || new Date()

            // Validate Time value
            if (
                !valueText ||
                isNaN(valueText) ||
                (!parseInt(valueText) && parseInt(valueText) !== 0)
            ) {
                return
            }

            let input
            if (calendarRef?.current) {
                input = calendarRef.current?.getInput()
            }

            if (type === 'hour') {
                let hour =
                    parseInt(valueText) < 0
                        ? 0
                        : parseInt(valueText) > 23
                        ? 23
                        : parseInt(valueText)

                if (hour || hour === 0) {
                    dateValue.setHours(hour)
                }

                if (dateValue.getTime() > Date.now()) {
                    timePickerElement.innerText = dateValue.getHours() + ''
                } else {
                    timePickerElement.innerText = new Date().getHours() + ''
                    dateValue = new Date()
                }
            } else if (type === 'minute') {
                let minute =
                    parseInt(valueText) < 0
                        ? 0
                        : parseInt(valueText) > 59
                        ? 59
                        : parseInt(valueText)

                if (minute || minute === 0) {
                    dateValue.setMinutes(minute)
                }

                if (dateValue.getTime() > Date.now()) {
                    timePickerElement.innerText = dateValue.getMinutes() + ''
                } else {
                    timePickerElement.innerText = new Date().getMinutes() + ''
                    dateValue = new Date()
                }
            }

            // On Change Time
            if (key === 'dateTime') {
                input.value = moment(dateValue).format('DD-MM-yyyy HH:mm') // Set Value for Form's Input
            } else if (key === 'time') {
                input.value = moment(dateValue).format('HH:mm') // Set Value for Form's Input
            }
            handleOnCalendarChange(key, dateValue)
        }

        const handleOnShowCalendar = key => {
            const inputHour =
                document.getElementsByClassName('p-hour-picker')[0]

            const inputMinute =
                document.getElementsByClassName('p-minute-picker')[0]

            // Get Default Value
            let hourDefaultValue
            let minuteDefaultValue
            if (data.value && data.value?.id && data.value?.start) {
                hourDefaultValue = data.value?.start?.slice(-5, -3)
                minuteDefaultValue = data.value?.start?.slice(-2)
            }

            // Add Editable Content & Style
            const styleObject = {
                padding: '8px',
                backgroundColor: '#e9e9e9',
                borderRadius: '4px',
                minWidth: '14px',
            }

            // Setting Element editable of PrimeReact Time Picker
            const handleInputTimeElement = (element, type) => {
                for (let i = 0; i < element.children.length; i++) {
                    let child = element.children[i]

                    if (child.tagName === 'SPAN') {
                        child.setAttribute('contentEditable', 'true')

                        Object.assign(child.style, styleObject)

                        child.setAttribute(
                            'innerText',
                            type === 'hour'
                                ? hourDefaultValue
                                : minuteDefaultValue,
                        )

                        // Add Event for Editable Content
                        child.addEventListener('blur', e => {
                            let timeText = e?.currentTarget?.innerText

                            // Handle Change
                            handleOnChangeTime(child, timeText, key, type)
                        })

                        child.addEventListener('input', e => {
                            let time = parseInt(
                                child.innerText.trim().replace(/[^\d]/g, ''),
                            )

                            if (type === 'hour') {
                                child.innerText =
                                    time || time === 0
                                        ? time <= 0
                                            ? '00'
                                            : time > 23
                                            ? '23'
                                            : time
                                        : '00'
                            } else if (type === 'minute') {
                                child.innerText =
                                    time || time === 0
                                        ? time <= 0
                                            ? '00'
                                            : time > 59
                                            ? '59'
                                            : time
                                        : '00'
                            }

                            // Put the cursor at the end of input
                            child.focus()

                            let range = document.createRange()
                            let selection = window.getSelection()
                            range.selectNodeContents(child)
                            range.collapse(false)
                            selection.removeAllRanges()
                            selection.addRange(range)
                        })

                        break
                    }
                }
            }

            if (inputHour) {
                handleInputTimeElement(inputHour, 'hour')
            }

            if (inputMinute) {
                handleInputTimeElement(inputMinute, 'minute')
            }
        }

        const handleShowForm = useCallback(
            (showData = {type: reminderTypes.reminder}) => {
                blockOverflownScroll()
                visible.value = true
                let holderData = {}

                if (!showData?.id) {
                    if (
                        showData[reminderKeys.type] === reminderTypes.reminder
                    ) {
                        holderData = {
                            ...initialReminderData,
                            userId: currentViewedUser?.id,
                        }
                    } else if (
                        showData[reminderKeys.type] ===
                        reminderTypes.appointment
                    ) {
                        holderData = {
                            ...initialAppointmentData,
                            userId:
                                calendarFormType === CalendarFormType.patient
                                    ? currentViewedUser?.id
                                    : null,
                        }
                    }
                } else {
                    if (
                        showData[reminderKeys.type] === reminderTypes.reminder
                    ) {
                        let dateTimeWithClientTZ = moment.tz(
                            showData.start,
                            timezoneId,
                        )
                        let userTz =
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                        let localStartDate = dateTimeWithClientTZ
                            .clone()
                            .tz(userTz)

                        let item = {
                            [reminderKeys.frequency]:
                                showData?.frequencyOptions?.selectedValue || [],
                            [reminderKeys.otherFrequency]:
                                showData?.frequencyOptions?.other || null,
                            [reminderKeys.instructions]:
                                showData?.instructionsOptions?.selectedValue ||
                                [],
                            [reminderKeys.otherInstructions]:
                                showData?.instructionsOptions?.other || null,
                            [reminderKeys.dateTime]: localStartDate
                                ? new Date(localStartDate)
                                : null,
                            [reminderKeys.oneTime]: true,
                        }

                        holderData = {...showData, ...item}
                    }

                    if (
                        showData[reminderKeys.type] ===
                        reminderTypes.appointment
                    ) {
                        let dateTimeWithClientTZ = moment.tz(
                            showData?.start,
                            timezoneId,
                        )
                        let userTz =
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                        let localStartDate = dateTimeWithClientTZ
                            .clone()
                            .tz(userTz)

                        let item = {
                            [reminderKeys.dateTime]: localStartDate
                                ? new Date(localStartDate)
                                : null,
                        }

                        holderData = {...showData, ...item}
                    }

                    let addList = []
                    if (showData?.frequencyOptions?.other) {
                        addList.push(reminderKeys.otherFrequency)
                    }
                    if (showData?.instructionsOptions?.other) {
                        addList.push(reminderKeys.otherInstructions)
                    }
                    if (showData.earlySendReminder) {
                        addList = [
                            ...addList,
                            reminderKeys.timeOrDay,
                            reminderKeys.earlyReminder,
                        ]
                    }

                    additionalKeys.value = addList
                }

                data.value = holderData
                // data.value = showData
                //     ? {...data.value, ...showData}
                //     : {...initialReminderData, userId: id}
            },
            [
                additionalKeys,
                calendarFormType,
                currentViewedUser?.id,
                data,
                timezoneId,
                visible,
            ],
        )

        const handleHideForm = () => {
            visible.value = false
            data.value = {}
            additionalKeys.value = []
            switchRef.current = []
            unblockOverflownScroll()
        }

        const handleOnToggleWitchesChange = (additionalKey, value) => {
            let holderList = isArrayAndNotEmpty(additionalKeys.value)
                ? [...additionalKeys.value]
                : []
            if (value) {
                if (additionalKey === reminderKeys.isRemindEarly) {
                    holderList = [
                        ...holderList,
                        reminderKeys.timeOrDay,
                        reminderKeys.earlyReminder,
                    ]
                    data.value[reminderKeys.isRemindEarly] = value
                } else {
                    holderList.push(additionalKey)
                }
            } else {
                if (additionalKey === reminderKeys.isRemindEarly) {
                    holderList = holderList.filter(
                        x =>
                            x !== reminderKeys.timeOrDay &&
                            x !== reminderKeys.earlyReminder,
                    )
                    data.value[reminderKeys.isRemindEarly] = value
                } else {
                    holderList = holderList.filter(x => x !== additionalKey)
                }
            }

            additionalKeys.value = holderList
        }

        const handleOnChange = (key, value) => {
            let holderData = data.value ? {...data.value} : {}
            if (key === reminderKeys.type) {
                if (value === reminderTypes.appointment) {
                    if (switchRef?.current) {
                        let otherKeys = [
                            reminderKeys.otherFrequency,
                            reminderKeys.otherInstructions,
                        ]
                        otherKeys.forEach(key => {
                            if (switchRef?.current[key]) {
                                switchRef.current.setValue(false)
                            }
                            handleOnToggleWitchesChange(key, false)
                        })
                    }
                } else if (value === reminderTypes.reminder) {
                    holderData[reminderKeys.oneTime] = true
                    if (switchRef?.current) {
                        if (switchRef?.current[reminderKeys.oneTime]) {
                            switchRef.current[reminderKeys.oneTime].setValue(
                                true,
                            )
                        }
                    }
                }
            }

            data.value = {...holderData, [key]: value}
        }

        const handleOnCalendarChange = (key, value) => {
            let holderData = data.value ? {...data.value} : {}
            if (key === reminderKeys.startDate) {
                let startDate = new Date(value)
                let endDate = holderData[reminderKeys.endDate]
                    ? new Date(holderData[reminderKeys.endDate])
                    : new Date()
                let time = holderData[reminderKeys.time]
                    ? new Date(holderData[reminderKeys.time])
                    : new Date()

                startDate.setHours(time.getHours())
                startDate.setMinutes(time.getMinutes())
                if (startDate < new Date()) {
                    startDate.setHours(new Date().getHours())
                    startDate.setMinutes(new Date().getMinutes())
                    endDate.setHours(new Date().getHours())
                    endDate.setMinutes(new Date().getMinutes())
                } else {
                    endDate.setHours(time.getHours())
                    endDate.setMinutes(time.getMinutes())
                }

                if (endDate <= startDate.setDate(startDate.getDate() + 1)) {
                    holderData[reminderKeys.endDate] = startDate
                }
                holderData[reminderKeys.time] = startDate
            }

            if (key === reminderKeys.time) {
                let time = new Date(value)

                let startDate = new Date(holderData[reminderKeys.startDate])
                startDate.setHours(time.getHours())
                startDate.setMinutes(time.getMinutes())

                let endDate = new Date(holderData[reminderKeys.endDate])
                endDate.setHours(time.getHours())
                endDate.setMinutes(time.getMinutes())

                holderData = {...holderData, startDate, endDate}
            }

            data.value = {...holderData, [key]: value}
        }

        const isShowItem = (key, list = [], addList = []) => {
            let isShow = false
            if (isArrayAndNotEmpty(list)) {
                if (list.includes(data.value[reminderKeys.type])) {
                    isShow = true
                    let arr = [
                        reminderKeys.otherFrequency,
                        reminderKeys.otherInstructions,
                        reminderKeys.otherAppointmentType,
                        reminderKeys.timeOrDay,
                        reminderKeys.earlyReminder,
                    ]
                    if (
                        arr.includes(key) &&
                        !additionalKeys.value.includes(key)
                    ) {
                        isShow = false
                    }

                    if (
                        key === reminderKeys.dateTime &&
                        data.value[reminderKeys.type] ===
                            reminderTypes.reminder &&
                        !data.value[reminderKeys.oneTime]
                    ) {
                        isShow = false
                    }

                    if (
                        [
                            reminderKeys.time,
                            reminderKeys.startDate,
                            reminderKeys.endDate,
                        ].includes(key) &&
                        data.value[reminderKeys.oneTime]
                    ) {
                        isShow = false
                    }
                }
            }
            return isShow
        }

        const handleDeleteMedical = async () => {
            const responseStatus = await deleteReminder(data.value)

            if (responseStatus === HTTP_STATUS_OK) {
                const {reminderKeys} = QueryKeyManager(
                    QueryKeyTypes.reminder,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries({
                    queryKey: reminderKeys.pattern(),
                })

                handleHideForm()
            }
        }

        const handleOnImage = (key, imageUrls) => {
            imgRef.current['image'].value = imageUrls
        }

        const convertTimeToUTC = time => {
            let timeUtc = null
            if (time) {
                let convertTimeToUtc = moment(time).utc()
                timeUtc = convertTimeToUtc
                    .toISOString()
                    .slice(0, 16)
                    .replace('T', ' ')
            }

            return timeUtc
        }

        const handleOnSubmit = useDebouncedCallback(async () => {
            let toPostData = {
                ...data.value,
                title: 'Reminder',
                image: imgRef?.current['image']?.value || null,
                // recipient: recipient,
                ownerId: user?.id,
                hospitalId: data?.value?.hospitalId
                    ? data?.value?.hospitalId
                    : currentViewedUser?.hospitalId,
            }

            if (data.value[reminderKeys.type] === reminderTypes.reminder) {
                // time
                if (data.value[reminderKeys.oneTime]) {
                    let currentDateTime = data.value?.dateTime
                        ? data.value?.dateTime
                        : new Date()
                    let now = new Date()

                    if (currentDateTime < now) {
                        showNotification(
                            severityNotification.warn,
                            'Please ensure that the Date Time is not earlier than the current time.',
                        )
                        return
                    }

                    toPostData = {
                        ...toPostData,
                        startDate: moment(currentDateTime).format(dateFormat),
                        startTime: moment(currentDateTime).format(timeFormat),
                        endDate: moment(currentDateTime).format(dateFormat),
                        endTime: moment(currentDateTime).format(timeFormat),
                        startDateTime: convertTimeToUTC(currentDateTime),
                        endDateTime: convertTimeToUTC(currentDateTime),
                    }
                } else {
                    let start = data.value.startDate
                        ? new Date(data.value.startDate)
                        : new Date()

                    let now = new Date()
                    if (start < now) {
                        return showNotification(
                            severityNotification.error,
                            'Please ensure that the Date Time is not earlier than the current time.',
                        )
                    }
                    let end = data.value.endDate
                        ? new Date(data.value.endDate)
                        : new Date()
                    end.setDate(end.getDate() + 1)

                    let dateArray = []
                    while (start < end) {
                        dateArray.push(new Date(start))
                        start.setDate(start.getDate() + 1)
                    }
                    if (dateArray.length > 0) {
                        let range = []

                        for (let i = 0; i < dateArray.length; i++) {
                            let startDateTime = new Date(dateArray[i])
                            let time = data.value.time
                                ? new Date(data.value.time)
                                : new Date()
                            startDateTime.setHours(time.getHours())
                            startDateTime.setMinutes(time.getMinutes())

                            let item = {
                                dateTime:
                                    moment(startDateTime).format(
                                        dateTimeFormat,
                                    ),
                                startDate:
                                    moment(startDateTime).format(dateFormat),
                                endDate:
                                    moment(startDateTime).format(dateFormat),
                                startDateTime: convertTimeToUTC(startDateTime),
                                time: moment(time).format(timeFormat),
                            }
                            range.push(item)
                        }

                        toPostData = {...toPostData, range}
                    }
                }

                // additional
                let additionalListKey = [
                    reminderKeys.frequency,
                    reminderKeys.instructions,
                ]
                for (let i = 0; i < additionalListKey.length; i++) {
                    let dataKey = dialogMedicineContent.find(
                        x => x.key === additionalListKey[i],
                    )

                    if (dataKey?.additionalKey) {
                        let holderItemList = isArrayAndNotEmpty(
                            data.value[additionalListKey[i]],
                        )
                            ? [...data.value[additionalListKey[i]]]
                            : []
                        if (data.value[dataKey?.additionalKey]) {
                            holderItemList.push(
                                data.value[dataKey?.additionalKey],
                            )
                        }

                        if (
                            isArrayAndNotEmpty(holderItemList) &&
                            dataKey?.configKey
                        ) {
                            let itemConfig = {
                                selectedValue:
                                    data.value[additionalListKey[i]] || [],
                                other: data.value[dataKey?.additionalKey],
                            }
                            toPostData = {
                                ...toPostData,
                                [additionalListKey[i]]:
                                    holderItemList.join(', '),
                                [dataKey?.configKey]: itemConfig,
                            }
                        }
                    }
                }
            }
            if (data.value[reminderKeys.type] === reminderTypes.appointment) {
                let currentDateTime = data.value?.dateTime
                    ? data.value?.dateTime
                    : new Date()

                if (currentDateTime.getTime() < Date.now()) {
                    showNotification(
                        severityNotification.warn,
                        'Please ensure that the Date Time is not earlier than the current time.',
                    )
                    return
                }
                let recipientDataList = []
                if (
                    isArrayAndNotEmpty(data.value[reminderKeys.recipient]) &&
                    isArrayAndNotEmpty(recipientsData)
                ) {
                    for (let i = 0; i < recipientsData.length; i++) {
                        if (isArrayAndNotEmpty(recipientsData[i].items)) {
                            let recipientItem = recipientsData[i].items.filter(
                                x =>
                                    data.value[reminderKeys.recipient].includes(
                                        x.value,
                                    ),
                            )
                            if (recipientItem.length > 0) {
                                let mapData = recipientItem.map(x => x.data)
                                recipientDataList = [
                                    ...recipientDataList,
                                    ...mapData,
                                ]
                            }
                        }
                    }
                }

                toPostData = {
                    ...toPostData,
                    recipientData: recipientDataList,
                    frequency: null,
                    instructions: null,
                    startDate: moment(currentDateTime).format(dateFormat),
                    startTime: moment(currentDateTime).format(timeFormat),
                    endDate: moment(currentDateTime).format(dateFormat),
                    endTime: moment(currentDateTime).format(timeFormat),
                    startDateTime: convertTimeToUTC(currentDateTime),
                    endDateTime: convertTimeToUTC(currentDateTime),
                }
            }
            const reminderData = await createOrUpdateReminder(toPostData)

            if (!!reminderData && reminderData?.length > 0) {
                const {reminderKeys} = QueryKeyManager(
                    QueryKeyTypes.reminder,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries({
                    queryKey: reminderKeys.pattern(),
                })

                handleHideForm()
            }

            // mutate(toPostData)
        }, [1000])
        //#endregion

        //#region Multiselect Template
        const groupedItemTemplate = option => {
            return (
                <div
                    className="flex align-items-center gap-2 "
                    style={{
                        color:
                            option.userType === userType.user
                                ? 'darkcyan'
                                : option.userType ===
                                  userType.generalPractitioner
                                ? 'var(--blue-500)'
                                : option.userType === userType.nextOfKin
                                ? 'var(--purple-500)'
                                : 'var(--blue-500)',
                    }}>
                    {option.userType && <i className="pi pi-user" />}
                    <div>{option.label}</div>{' '}
                    {option.userType && (
                        <div>({convertKeyToLabel(option.userType)}) </div>
                    )}
                </div>
            )
        }

        const panelFooterTemplate = e => {
            return e.name === reminderKeys.recipient ? (
                <div className="flex gap-2 bg-gray-50 p-2">
                    <div className="flex gap-1 align-items-center  border-green-100 text-green-500  bg-green-50 border-1 p-1 px-2 border-round-sm">
                        <i className="pi pi-check " />
                        <p className="text-sm font-semibold">
                            Available or active device
                        </p>
                    </div>
                    <div className="flex gap-1 align-items-center border-orange-100 border-1 text-orange-500 bg-orange-50 p-1 px-2 border-round-sm">
                        <i className=" pi pi-exclamation-triangle " />{' '}
                        <p className="text-sm font-semibold">
                            Unavailable or inactive device
                        </p>
                    </div>
                </div>
            ) : (
                <p className="bg-gray-50 p-2 text-sm font-semibold">
                    {isArrayAndNotEmpty(e.value) ? e.value.length : 0} items
                    selected
                </p>
            )
        }

        const selectedList = useMemo(() => {
            let holderList = []

            let selectedRecipient = []

            if (!recipientsData || !recipientsData.length) {
                return holderList
            }

            if (
                !data.value[reminderKeys.recipient] ||
                !data.value[reminderKeys.recipient].length
            ) {
                return holderList
            }

            selectedRecipient = recipientsData.map(group => {
                const matchingItems = group.items.filter(
                    item =>
                        data.value[reminderKeys.recipient].includes(
                            item.value,
                        ) ||
                        data.value[reminderKeys.recipient].some(x => {
                            if (x.available) {
                                return (
                                    convertKeyToLabel(x.sendMethod) ===
                                    item.label
                                )
                            }
                        }),
                )

                const methodList = matchingItems.map(x => x.label)
                return {
                    label: group.label,
                    userType: group.userType,
                    methodsCount: matchingItems.length,
                    method: methodList,
                }
            })

            if (selectedRecipient?.length) {
                holderList = selectedRecipient.filter(x => x?.methodsCount)
            }
            return holderList
        }, [data.value, recipientsData])

        const selectedItemTemplate = useCallback(
            e => {
                if (
                    isArrayAndNotEmpty(data.value[reminderKeys.recipient]) &&
                    data.value[reminderKeys.recipient][0] === e
                ) {
                    return (
                        <div className="flex gap-2 p-1">
                            {selectedList.map((item, idx) => (
                                <div
                                    className="p-1 px-2 text-white border-round-sm"
                                    key={idx}
                                    style={{
                                        background:
                                            item.userType === userType.user
                                                ? 'darkcyan'
                                                : item.userType ===
                                                  userType.generalPractitioner
                                                ? 'var(--blue-500)'
                                                : item.userType ===
                                                  userType.nextOfKin
                                                ? 'var(--purple-500)'
                                                : '',
                                    }}>
                                    {item.label}
                                    <span className="font-bold">
                                        ({item.methodsCount})
                                    </span>
                                </div>
                            ))}
                        </div>
                    )
                }
                return null
            },
            [data.value, selectedList],
        )

        const multiselectItemTemplate = item => {
            return (
                <div className="flex gap-2 align-items-center ">
                    {item.userType && (
                        <div>
                            {' '}
                            {item.disabled ? (
                                <i className="pi pi-exclamation-triangle text-orange-500 bg-orange-50 p-1 border-round-sm" />
                            ) : (
                                <i className="pi pi-check text-green-500 bg-green-50 p-1 border-round-sm" />
                            )}{' '}
                        </div>
                    )}

                    <div>{item.label}</div>
                </div>
            )
        }

        //#endregion

        //#region Template
        const onConfirmDeletePopup = event => {
            confirmPopup({
                target: event.currentTarget,
                message: 'Do you want to delete this?',
                icon: 'pi pi-info-circle',
                defaultFocus: 'reject',
                acceptClassName: 'p-button-danger',
                accept: e => handleDeleteMedical(),
            })
        }

        const renderFooter = name => {
            let isActive = true
            if (data.value?.id && !data.value.isReschedule) {
                isActive = false
            }

            let isDeleted = false
            if (
                data.value?.id &&
                (data.value.type === 'reminder' ||
                    (!data.value.isReschedule &&
                        user?.id === data.value?.ownerId))
            ) {
                isDeleted = true
            }

            return (
                <div className="">
                    <div className="text-left px-3">
                        <h5 className={customLabelClassName}>
                            {translateCommons(
                                `${convertKeyToLabel(
                                    data?.value[reminderKeys.type],
                                )} Summary`,
                            )}
                        </h5>

                        <CalendarFormSummary
                            data={data.value}
                            recipientSelectedList={selectedList.value}
                            patientName={
                                data.value?.id
                                    ? data.value?.name
                                    : currentViewedUser?.fullName
                            }
                        />
                    </div>
                    <div>
                        {' '}
                        {isDeleted && (
                            <React.Fragment>
                                <Button
                                    label={translateCommons('Delete')}
                                    icon="pi pi-trash"
                                    className="p-button-text p-button-danger"
                                    onClick={onConfirmDeletePopup}
                                    autoFocus
                                />
                                <ConfirmPopup />
                            </React.Fragment>
                        )}
                        <Button
                            label={translateCommons('Close')}
                            icon="pi pi-times"
                            onClick={handleHideForm}
                            className="p-button-text"
                        />
                        <Button
                            label={translateCommons('Submit')}
                            icon="pi pi-check"
                            onClick={handleOnSubmit}
                            autoFocus
                        />
                    </div>
                </div>
            )
        }

        const getTimeDataWithTz = (
            key,
            dateTime,
            pattern = 'DD-MM-YYYY HH:mm',
        ) => {
            const dataTime = dateTime ? moment(dateTime) : moment()

            const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone

            const dateWithClientTz = moment.tz(
                dataTime.format(pattern),
                timezoneId,
            )
            const convertedUTCDate = dateWithClientTz.utc()
            const dateWithUserTz = convertedUTCDate.clone().tz(userTz)

            return {
                clientTz: `${timezoneId} GMT ${dataTime
                    .tz(timezoneId)
                    .format('Z')}`,
                clientTime: dataTime.format(`${pattern}`),
                userTz: `${userTz} GMT ${dataTime.tz(userTz).format('Z')}`,
                userTime: dataTime.format(pattern),
            }
        }

        const renderCalendarFooter = key => {
            if (!data.value[key] || key === reminderKeys.time) {
                return null
            }

            let dataTimeTz = getTimeDataWithTz(key, data.value[key])

            return (
                <div className="col-12">
                    {key !== reminderKeys.time && (
                        <React.Fragment>
                            {' '}
                            <div className="font-semibold flex justify-content-center">
                                <p className="mr-2">
                                    {translateCommons(
                                        "Date time based on Client's time zone ",
                                    )}{' '}
                                    ({dataTimeTz.clientTz}):{' '}
                                    <span className="text-primary">
                                        {dataTimeTz.clientTime}{' '}
                                    </span>
                                </p>
                            </div>
                            <div className="font-semibold flex justify-content-center">
                                <p className="mr-2">
                                    {translateCommons(
                                        'Your current date time ',
                                    )}{' '}
                                    ({dataTimeTz.userTz}
                                    ):{' '}
                                    <span className="text-primary">
                                        {dataTimeTz.userTime}
                                    </span>
                                </p>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            )
        }

        const renderItemTemplate = useCallback(item => {
            return (
                <div className={'flex align-items-center'}>
                    <div className="mr-3">
                        <Avatar
                            className="avatar-sm"
                            src={
                                item?.extraData ||
                                'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png'
                            }
                        />
                    </div>
                    <div className={'flex flex-column user-convo'}>
                        <h6 className="font-bold">{item.label}</h6>
                        {item.data ? <div>{item.data}</div> : null}
                    </div>
                </div>
            )
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        const reminderTypeRender = useMemo(() => {
            if (calendarFormType === CalendarFormType.reservation) {
                return [reminderTypes.appointment]
            }

            return Object.keys(reminderTypes)
        }, [calendarFormType])

        const minDate = useComputed(() => {
            let minDate = {
                [reminderKeys.dateTime]: new Date(),
                [reminderKeys.startDate]: new Date(),
                [reminderKeys.endDate]: new Date(),
                [reminderKeys.time]: new Date(),
            }

            if (data.value?.id && data.value[reminderKeys.dateTime]) {
                let dateTime = new Date(data.value[reminderKeys.dateTime])

                if (dateTime.getTime() < Date.now()) {
                    minDate[reminderKeys.dateTime] = new Date()
                }
            }

            if (data.value[reminderKeys.startDate]) {
                const nextDay = new Date(data.value[reminderKeys.startDate])
                nextDay.setDate(nextDay.getDate() + 1)

                minDate[reminderKeys.endDate] = new Date(nextDay)
            } else {
                const nextDay = new Date()
                nextDay.setDate(nextDay.getDate() + 1)

                minDate[reminderKeys.endDate] = new Date(nextDay)
            }

            return minDate
        })

        //#endregion

        // Current Date Value
        const handleGetDateValue = useCallback(date => {
            // Check if data isNull or isBefore Current Now
            // if (
            //     !date ||
            //     !date?.getTime() ||
            //     date.getTime() < new Date().getTime()
            // ) {
            //     return null
            // }

            return date
        }, [])

        return (
            <React.Fragment>
                <Dialog
                    maskClassName="custom-z-index"
                    className=""
                    header={
                        data.value?.id
                            ? translateCommons(
                                  `${
                                      data.value.type
                                          ? capitalize(data.value.type)
                                          : 'Reminder'
                                  } Summary`,
                              )
                            : translateCommons(
                                  `Add ${
                                      data.value.type
                                          ? capitalize(data.value.type)
                                          : 'Reminder'
                                  }`,
                              )
                    }
                    visible={visible.value}
                    style={{width: '40vw'}}
                    breakpoints={{
                        '1400px': '50vw',
                        '1200px': '60vw',
                        '1000px': '100vw',
                    }}
                    footer={renderFooter('displayBasic')}
                    onHide={handleHideForm}
                    blockScroll>
                    {/* Type */}
                    {!data.value?.id && (
                        <div className="col-12 px-3">
                            <div className="flex justify-content-between w-full">
                                <div>
                                    <h5 className={customLabelClassName}>
                                        {translateCommons('Type')}
                                    </h5>
                                </div>
                                {!data.value?.id &&
                                    data?.value[reminderKeys.type] ===
                                        reminderTypes.reminder && (
                                        <div className="">
                                            <CustomToggleWitches
                                                ref={el =>
                                                    (switchRef.current[
                                                        reminderKeys.oneTime
                                                    ] = el)
                                                }
                                                defaultValue={
                                                    data.value.oneTime || true
                                                }
                                                onChange={e => {
                                                    handleOnChange(
                                                        reminderKeys.oneTime,
                                                        e,
                                                    )
                                                }}
                                            />
                                        </div>
                                    )}
                            </div>
                            <div className="flex gap-2 ">
                                {reminderTypeRender?.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className={`darkcyan-button ${
                                            data.value[reminderKeys.type] ===
                                                item && 'active'
                                        } w-13rem p-3 border-round-md cursor-pointer`}
                                        onClick={() =>
                                            handleOnChange(
                                                reminderKeys.type,
                                                item,
                                            )
                                        }>
                                        <div className="flex justify-content-between">
                                            <div className="flex gap-2">
                                                <i
                                                    className={
                                                        item ===
                                                        reminderTypes.reminder
                                                            ? 'pi pi-clock'
                                                            : 'pi pi-calendar-plus'
                                                    }
                                                />{' '}
                                                <h6 className="m-0 p-0 text-800 font-bold">
                                                    {convertKeyToLabel(item)}
                                                </h6>
                                            </div>
                                            <div>
                                                <div
                                                    className="darkcyan-button-select p-1 border-circle border-1 "
                                                    style={{
                                                        width: '1.2rem',
                                                        height: '1.2rem',
                                                    }}></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Search */}
                    {calendarFormType === CalendarFormType.reservation && (
                        <div className="grid col-12 p-2">
                            <div className="col-12">
                                <h5 className={customLabelClassName + ' m-0'}>
                                    Schedule for
                                </h5>
                            </div>

                            <StyledPatientOptions
                                value={data.value['userId'] || null}
                                onChange={selectedItem => {
                                    data.value = {
                                        ...data.value,
                                        userId: selectedItem?.value,
                                    }
                                }}
                            />

                            {/* <AutoComplete
                                id="custom"
                                field="label"
                                inputClassName="w-full"
                                className="no-outline mb-0 col-12"
                                placeholder={translateCommons(
                                    'Enter username or email',
                                )}
                                suggestions={[]}
                                completeMethod={() => {}}
                                value={null}
                                onChange={e => {}}
                                onSelect={({value: data}) => {}}
                                itemTemplate={renderItemTemplate}
                                autoFocus
                                forceSelection
                            /> */}
                        </div>
                    )}

                    {/* FORM CONTENT */}
                    <div className="grid col-12">
                        {dialogMedicineContent.map(
                            (item, idx) =>
                                isShowItem(item.key, item.isActive) && (
                                    <div
                                        key={idx}
                                        className={`${
                                            item.className || 'col-12'
                                        } mb-1`}>
                                        <div className="flex justify-content-between">
                                            <div>
                                                {' '}
                                                <h5
                                                    className={
                                                        customLabelClassName
                                                    }>
                                                    {translateCommons(
                                                        item.label,
                                                    )}
                                                </h5>
                                            </div>
                                            {!data.value?.id &&
                                                item.additionalKey && (
                                                    <div
                                                        className={`${
                                                            item.key ===
                                                                reminderKeys.dateTime &&
                                                            data.value[
                                                                reminderKeys
                                                                    .type
                                                            ] ===
                                                                reminderTypes.reminder
                                                                ? 'hidden'
                                                                : ''
                                                        }`}>
                                                        <CustomToggleWitches
                                                            ref={el =>
                                                                (switchRef.current[
                                                                    item.key
                                                                ] = el)
                                                            }
                                                            defaultValue={
                                                                item.key ===
                                                                reminderKeys.frequency
                                                                    ? !!data
                                                                          .value
                                                                          .frequencyOptions
                                                                    : item.key ===
                                                                          reminderKeys.instructions &&
                                                                      !!data
                                                                          .value
                                                                          .instructionsOptions
                                                            }
                                                            label={
                                                                item.additionalLabel ||
                                                                'Additional'
                                                            }
                                                            onChange={e => {
                                                                handleOnToggleWitchesChange(
                                                                    item.additionalKey,
                                                                    e,
                                                                )
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                            {!data.value?.id &&
                                                !!item.required && (
                                                    <div>
                                                        <Tag
                                                            value={translateCommons(
                                                                'This field is required !',
                                                            )}
                                                            severity="danger"
                                                            icon="pi pi-info-circle"></Tag>
                                                    </div>
                                                )}
                                        </div>
                                        <div>
                                            {item.component ===
                                            componentTypes.input ? (
                                                <InputText
                                                    className="col-12 font-bold"
                                                    style={{
                                                        height: '40px',
                                                    }}
                                                    name={`${item.key}`}
                                                    value={
                                                        data.value[item.key] ||
                                                        ''
                                                    }
                                                    defaultValue={
                                                        item.initialValue
                                                    }
                                                    placeholder={`${translateCommons(
                                                        'Input',
                                                    )} ${translateCommons(
                                                        item.label,
                                                    )} ${
                                                        !!item?.required
                                                            ? '- ' +
                                                              translateCommons(
                                                                  'Field is required',
                                                              )
                                                            : ''
                                                    }`}
                                                    onChange={e =>
                                                        handleOnChange(
                                                            item.key,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            ) : item.component ===
                                              componentTypes.dropdown ? (
                                                <Dropdown
                                                    className="col-12 p-0 m-0 font-bold"
                                                    name={`${item.key}`}
                                                    style={{height: '40px'}}
                                                    filter
                                                    emptyFilterMessage={translateCommons(
                                                        'No available options',
                                                    )}
                                                    emptyMessage={translateCommons(
                                                        'No results found',
                                                    )}
                                                    filterBy="label"
                                                    value={
                                                        data.value[item.key] ||
                                                        item.defaultValue
                                                    }
                                                    defaultValue={
                                                        item.defaultValue
                                                    }
                                                    options={
                                                        item.key ===
                                                        reminderKeys.earlyReminder
                                                            ? getEarlyReminderOptions(
                                                                  data.value[
                                                                      reminderKeys
                                                                          .timeOrDay
                                                                  ],
                                                              )
                                                            : item.options
                                                    }
                                                    onChange={e => {
                                                        handleOnChange(
                                                            item.key,
                                                            e.target.value,
                                                        )
                                                    }}
                                                    placeholder={`${translateCommons(
                                                        'Select',
                                                    )} ${translateCommons(
                                                        item.label,
                                                    )}`}
                                                    disabled={!!data.value.id}
                                                />
                                            ) : item.component ===
                                              componentTypes.calendar ? (
                                                <Calendar
                                                    locale={
                                                        getLanguageCode() ===
                                                        'gb'
                                                            ? 'en'
                                                            : getLanguageCode()
                                                    }
                                                    className="col-12 px-0 font-bold"
                                                    ref={calendarRef}
                                                    inputClassName="font-bold text-left"
                                                    showIcon
                                                    timeOnly={
                                                        item?.key === 'time'
                                                    }
                                                    icon={
                                                        item?.key === 'time'
                                                            ? 'pi pi-clock'
                                                            : 'pi pi-calendar'
                                                    }
                                                    name={`${item.key}`}
                                                    value={
                                                        data.value[item.key]
                                                            ? handleGetDateValue(
                                                                  data.value[
                                                                      item.key
                                                                  ],
                                                              )
                                                            : item.key ===
                                                              'endDate'
                                                            ? new Date(
                                                                  new Date().setDate(
                                                                      new Date().getDate() +
                                                                          1,
                                                                  ),
                                                              )
                                                            : new Date()
                                                    }
                                                    onChange={e =>
                                                        handleOnCalendarChange(
                                                            item.key,
                                                            e.value,
                                                        )
                                                    }
                                                    onShow={() =>
                                                        handleOnShowCalendar(
                                                            item.key,
                                                        )
                                                    }
                                                    showTime={
                                                        item.showTime || false
                                                    }
                                                    hourFormat="24"
                                                    dateFormat="dd-mm-yy"
                                                    minDate={
                                                        minDate.value[item.key]
                                                    }
                                                    stepHour={1}
                                                    stepMinute={1}
                                                    // hideOnDateTimeSelect={true}
                                                    placeholder={`${translateCommons(
                                                        'Select',
                                                    )}${translateCommons(
                                                        item.label,
                                                    )}`}
                                                    footerTemplate={() =>
                                                        renderCalendarFooter(
                                                            item?.key,
                                                        )
                                                    }
                                                    timeOnly={item.timeOnly}
                                                    // disabled={
                                                    //     data.value?.id &&
                                                    //     !data.value.isReschedule
                                                    // }
                                                />
                                            ) : item.component ===
                                              componentTypes.multiSelect ? (
                                                <MultiSelect
                                                    className="col-12 p-0 m-0 font-bold"
                                                    name={item.key}
                                                    style={{height: '40px'}}
                                                    display="chip"
                                                    filter
                                                    filterBy="label"
                                                    value={data.value[item.key]}
                                                    options={item.options || []}
                                                    optionLabel="label"
                                                    optionGroupLabel="label"
                                                    optionGroupChildren="items"
                                                    optionGroupTemplate={
                                                        groupedItemTemplate
                                                    }
                                                    panelFooterTemplate={
                                                        panelFooterTemplate
                                                    }
                                                    onChange={e =>
                                                        handleOnChange(
                                                            item.key,
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder={`${translateCommons(
                                                        'Select',
                                                    )} ${translateCommons(
                                                        item.label,
                                                    )}`}
                                                    // disabled={!!data.value.id}
                                                />
                                            ) : item.component ===
                                              componentTypes.recipient ? (
                                                <MultiSelect
                                                    className="col-12 p-0 m-0 font-bold"
                                                    style={{height: '40px'}}
                                                    display="chip"
                                                    filter
                                                    filterBy="filterKey"
                                                    value={data.value[item.key]}
                                                    itemTemplate={
                                                        multiselectItemTemplate
                                                    }
                                                    options={recipientsData}
                                                    optionLabel="label"
                                                    optionGroupLabel="label"
                                                    optionGroupChildren="items"
                                                    optionGroupTemplate={
                                                        groupedItemTemplate
                                                    }
                                                    panelFooterTemplate={
                                                        panelFooterTemplate
                                                    }
                                                    selectedItemTemplate={e =>
                                                        selectedItemTemplate(e)
                                                    }
                                                    onChange={e =>
                                                        handleOnChange(
                                                            item.key,
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder={`${translateCommons(
                                                        'Select',
                                                    )} ${translateCommons(
                                                        item.label,
                                                    )}`}
                                                />
                                            ) : (
                                                item.component ===
                                                    componentTypes.inputArea && (
                                                    <InputTextarea
                                                        value={
                                                            data.value[item.key]
                                                        }
                                                        className="w-full"
                                                        rows={4}
                                                        onChange={e =>
                                                            handleOnChange(
                                                                item.key,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                )
                                            )}
                                        </div>
                                    </div>
                                ),
                        )}

                        {/* FORM IMAGE UPLOADER */}
                        {data.value[reminderKeys.type] &&
                            data.value[reminderKeys.type] !==
                                reminderTypes.appointment && (
                                <div className="col-12">
                                    <h5 className={customLabelClassName}>
                                        {translateCommons('Image')}
                                    </h5>
                                    <ImageUploader
                                        id={'image'}
                                        ref={el =>
                                            (imgRef.current['image'] = el)
                                        }
                                        callBack={handleOnImage}
                                        multiple={false}
                                        extraData={{}}
                                        initialImage={data.value?.image}
                                    />
                                </div>
                            )}
                    </div>
                </Dialog>
            </React.Fragment>
        )
    },
)

export default CalendarForm
